'use client';

import {usePathname} from 'next/navigation';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {v4} from 'uuid';

import {getSspAd} from '@web-catalog/api/sspApi/sspApi';

import {
  AdResponseType,
  useAdAnalytics,
} from '@web-catalog/components/Ads/useAdAnalytics';

import {useOmicronABFlag} from '@web-catalog/hooks';

import {
  authDoneStates,
  useAuthorizedProfile,
  useAuthProgressState,
} from '@web-catalog/store/auth';

import {
  safeLocalStorage,
  safeSessionStorage,
} from '@web-catalog/utils/safeStorages';

import {BLOCK_TIME, LAST_AD_SHOWN, SSP_SESSION_ID, SSP_USER_ID} from './consts';
import {SSPBanner} from './SSPBanner';
import {TSSPParsed} from './types';
import {checkIsLastShowExpired, getSppAdPayload} from './utils';

const callTrackingUrl = (url: string | null | undefined) => {
  if (url) {
    fetch(url, {mode: 'no-cors'}).catch(console.warn);
  }
};

export const SSP = ({
  slotId,
  setIsSSPShown,
}: {
  slotId: string;
  setIsSSPShown: Dispatch<SetStateAction<boolean>>;
}) => {
  const [isLastShowExpired, setIsLastShowExpired] = useState(
    checkIsLastShowExpired(),
  );
  const [isAdLoading, setIsAdLoading] = useState(true);
  const [adInfo, setAdInfo] = useState<TSSPParsed | null>(null);
  const userInfo = useAuthorizedProfile();
  const sspAdsAuthEnabled = useOmicronABFlag('WEB_SSP_AD_WITH_AUTH');
  const adLog = useAdAnalytics({platform: 'SSP', adType: 'sticky_banner'});

  const path = usePathname();

  const authProgressState = useAuthProgressState();
  const isAuthFinished = authDoneStates.includes(authProgressState);

  useEffect(() => {
    setIsLastShowExpired(checkIsLastShowExpired());
  }, [path]);

  useEffect(() => {
    if (sspAdsAuthEnabled && isAuthFinished && !userInfo?.id) {
      setIsSSPShown(false);
    }
  }, [isAuthFinished, setIsSSPShown, sspAdsAuthEnabled, userInfo?.id]);

  useEffect(() => {
    if (sspAdsAuthEnabled) {
      if (!userInfo?.id || !isLastShowExpired) {
        return;
      }
    } else {
      if (!isAuthFinished || !isLastShowExpired) {
        return;
      }
    }

    const sspUserId = safeLocalStorage.getItem(SSP_USER_ID) || v4();
    const sspSessionId = safeSessionStorage.getItem(SSP_SESSION_ID) || v4();

    if (!safeSessionStorage.getItem(SSP_SESSION_ID)) {
      safeSessionStorage.setItem(SSP_SESSION_ID, sspSessionId);
    }

    adLog.requested(slotId);

    getSspAd(
      getSppAdPayload({
        userVkId: userInfo?.id,
        sspSessionId,
        sspUserId,
        slotId,
      }),
    )
      .then((sspAd) => {
        const sspParsed: TSSPParsed = {
          ...sspAd,
          // если придет 204 с пустым ответом, дропнется и сработает setIsSSPShown
          creative: JSON.parse(sspAd.creative),
        };

        setAdInfo(sspParsed);
        adLog.gotResponse(sspParsed.slotid, {
          AdResponse: AdResponseType.HAS_ADS,
        });
        adLog.rendered(sspParsed.slotid);
        adLog.shown(sspParsed.slotid);

        // Все фетчи - это логирование рекламы
        callTrackingUrl(sspParsed.load);
        callTrackingUrl(sspParsed.impression);
        if (sspParsed.nurl) {
          fetch(sspParsed.nurl, {mode: 'no-cors'}).catch(() => {
            adLog.blocked(slotId);
          });
        }

        sspParsed.creative.native.imptrackers?.forEach(callTrackingUrl);

        sspParsed.creative.native.ext.imppixel?.forEach(callTrackingUrl);

        setTimeout(() => {
          setIsAdLoading(false);
        }, BLOCK_TIME);
      })
      .catch(() => {
        adLog.gotResponse(slotId, {AdResponse: AdResponseType.NO_ADS});
        adLog.blocked(slotId);
        adLog.nothingToShow(slotId);
        setIsSSPShown(false);
      });
  }, [
    slotId,
    userInfo?.id,
    isLastShowExpired,
    isAuthFinished,
    adLog,
    setIsSSPShown,
    sspAdsAuthEnabled,
  ]);

  const handleCloseAd = useCallback(() => {
    callTrackingUrl(adInfo?.creative.native.ext.adChoices?.closeUrl);

    safeLocalStorage.setItem(LAST_AD_SHOWN, String(Date.now()));

    setIsLastShowExpired(false);
    adLog.closed(slotId);
  }, [adInfo, adLog, slotId]);

  const handleOpenAd = useCallback(() => {
    callTrackingUrl(adInfo?.click);

    adInfo?.creative.native.link.clicktrackers?.forEach(callTrackingUrl);

    adLog.clicked(slotId);
    window.open(adInfo?.creative.native.link.url, '_blank');
  }, [slotId, adInfo, adLog]);

  if (!isLastShowExpired || !adInfo) {
    return null;
  }

  return (
    <SSPBanner
      adInfo={adInfo}
      isAdLoading={isAdLoading}
      onClose={handleCloseAd}
      onOpen={handleOpenAd}
    />
  );
};

export default SSP;
